import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface OwnershipTransferRestrictions {
  enabled: boolean;
  allow_only_company_users: boolean;
  allowed_domains: string[];
}

interface RecipientRestrictions {
  enabled: boolean;
  allow_only_company_users: boolean;
  allowed_domains: string[];
}

interface AssetProtectionSettings {
  ownership_transfer_restrictions?: OwnershipTransferRestrictions;
  recipient_restrictions?: RecipientRestrictions;
}

export const useAssetProtectionSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AssetProtectionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const protectionSettings = await apiService.getCompanyAssetProtectionSettings(user.company_id);
        setSettings(protectionSettings);
      } catch (err: any) {
        console.error('Failed to load asset protection settings:', err);
        setError('Failed to load asset protection settings');
        // Default to no restrictions if we can't load
        setSettings({
          ownership_transfer_restrictions: {
            enabled: false,
            allow_only_company_users: false,
            allowed_domains: [],
          },
          recipient_restrictions: {
            enabled: false,
            allow_only_company_users: false,
            allowed_domains: [],
          },
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.company_id]);

  // Helper to extract domain from email
  const extractDomain = (email: string): string | null => {
    const parts = email.split('@');
    return parts.length === 2 ? parts[1].toLowerCase() : null;
  };

  // Helper to check if domain is in allowed list (supports subdomains)
  const isDomainAllowed = (emailDomain: string, allowedDomains: string[]): boolean => {
    if (!emailDomain) return false;
    return allowedDomains.some(allowed => {
      const normalizedAllowed = allowed.toLowerCase().replace(/^@/, '');
      return emailDomain === normalizedAllowed || emailDomain.endsWith(`.${normalizedAllowed}`);
    });
  };

  // Helper to check if an email is allowed for ownership transfer
  // Returns { allowed: boolean, reason?: string }
  // Note: Frontend validation is lenient - we only block obvious cases
  // Backend will perform definitive validation
  const isEmailAllowedForOwnershipTransfer = (email: string): { allowed: boolean; reason?: string } => {
    if (!settings?.ownership_transfer_restrictions?.enabled) {
      return { allowed: true }; // No restrictions
    }

    const restrictions = settings.ownership_transfer_restrictions;
    const domain = extractDomain(email);
    
    if (!domain) {
      return { allowed: false, reason: 'Invalid email format' };
    }

    // If company-only mode, we can't reliably determine company membership on frontend
    // So we'll be lenient and allow it - backend will validate
    if (restrictions.allow_only_company_users) {
      // Frontend can't determine company membership reliably, so allow and let backend validate
      return { allowed: true };
    }

    // If custom allowed domains are specified, check if domain matches
    if (restrictions.allowed_domains && restrictions.allowed_domains.length > 0) {
      if (isDomainAllowed(domain, restrictions.allowed_domains)) {
        return { allowed: true };
      }
      // Domain not in allowed list - block it
      return { allowed: false, reason: `Ownership transfer is restricted to specific domains. '${domain}' is not allowed.` };
    }

    // Enabled but no specific rules - be lenient, let backend decide
    return { allowed: true };
  };

  // Helper to check if an email is allowed for recipient sharing
  // Returns { allowed: boolean, reason?: string }
  // Note: Frontend validation is lenient - we only block obvious cases
  // Backend will perform definitive validation
  const isEmailAllowedForRecipient = (email: string): { allowed: boolean; reason?: string } => {
    if (!settings?.recipient_restrictions?.enabled) {
      return { allowed: true }; // No restrictions
    }

    const restrictions = settings.recipient_restrictions;
    const domain = extractDomain(email);
    
    if (!domain) {
      return { allowed: false, reason: 'Invalid email format' };
    }

    // If company-only mode, we can't reliably determine company membership on frontend
    // So we'll be lenient and allow it - backend will validate
    if (restrictions.allow_only_company_users) {
      // Frontend can't determine company membership reliably, so allow and let backend validate
      return { allowed: true };
    }

    // If custom allowed domains are specified, check if domain matches
    if (restrictions.allowed_domains && restrictions.allowed_domains.length > 0) {
      if (isDomainAllowed(domain, restrictions.allowed_domains)) {
        return { allowed: true };
      }
      // Domain not in allowed list - block it
      return { allowed: false, reason: `Sharing is restricted to specific domains. '${domain}' is not allowed.` };
    }

    // Enabled but no specific rules - be lenient, let backend decide
    return { allowed: true };
  };

  return {
    settings,
    loading,
    error,
    isEmailAllowedForOwnershipTransfer,
    isEmailAllowedForRecipient,
  };
};
